import { useCallback } from 'react';
import UsageTracker from '../../utils/UsageTracker';
import { Flex } from 'foundations-components/components/Flex';
import FormattedMessage from 'I18n/components/FormattedMessage';
import LanguageNames from 'ui-addon-i18n/constants/LanguageNames';
// @ts-expect-error Untyped
import useLanguageRequested from '../../hooks/useLanguageRequests';
import { useFilterOptions } from '../../features/filters/filtersHooks';
import { FILTER_TYPES } from '../../constants/TopFilterConstants';
import { TRACKS_URL_FRAGMENT } from '../../constants/Navigation';
import { LESSONS_URL_FRAGMENT } from '../../constants/Navigation';
import { useHistory } from 'quartz-routing/react-router';
import { Button } from 'foundations-components/transitional/components/Button';
import { View } from 'foundations-components/transitional/components/View';
import { Select } from 'foundations-components/transitional/components/Select';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
const useLanguageSwitcher = ({
  defaultValue,
  languageLinkedContent
}) => {
  const history = useHistory();
  const languageCodes = useFilterOptions(FILTER_TYPES.LANGUAGE);
  const selectedLanguageCode = defaultValue === null || defaultValue === void 0 ? void 0 : defaultValue.toString().toLowerCase();
  const allowList = [...Object.keys(languageLinkedContent.languageToId).map(k => k.toLowerCase()), selectedLanguageCode];
  const options = languageCodes.map(code => {
    const languageCode = code.toLowerCase();
    return {
      disabled: !(allowList !== null && allowList !== void 0 && allowList.includes(languageCode)),
      text: LanguageNames[languageCode] || languageCode,
      value: languageCode
    };
  });
  const navigateToLanguage = useCallback(languageCode => {
    const languageBasePath = languageLinkedContent.linkedEntityType === 'LESSON' ? LESSONS_URL_FRAGMENT : TRACKS_URL_FRAGMENT;
    const contentId = languageLinkedContent.languageToId[languageCode.toUpperCase()];
    history.push(`${languageBasePath}/${contentId}?language=${languageCode}`);
    UsageTracker.trackStandalone('Filter Interaction', {
      action: 'filter-selected',
      value: languageCode,
      key: 'language-switcher',
      defaultValue
    });
  }, [history, languageLinkedContent, defaultValue]);
  const handleOptionSelection = useCallback(option => {
    if (option.disabled) {
      return;
    }
    navigateToLanguage(option.value);
  }, [navigateToLanguage]);
  const handleSelectChange = useCallback(changeEvent => {
    const {
      currentTarget: {
        value
      }
    } = changeEvent;
    navigateToLanguage(value);
  }, [navigateToLanguage]);
  const handleSelectOpen = useCallback(isOpen => {
    if (isOpen) {
      UsageTracker.trackStandalone('Filter Interaction', {
        action: 'filter-menu-opened',
        key: 'language-switcher',
        value: selectedLanguageCode || '',
        defaultValue: defaultValue === null || defaultValue === void 0 ? void 0 : defaultValue.toString().toLowerCase()
      });
    }
  }, [defaultValue, selectedLanguageCode]);
  return {
    options,
    selectedValue: selectedLanguageCode,
    handleOptionSelection,
    handleSelectChange,
    handleSelectOpen
  };
};
const MobileLanguageOption = ({
  option,
  trackId,
  lessonId,
  onSelect
}) => {
  const [languageRequested, setLanguageRequested] = useLanguageRequested({
    trackId,
    lessonId,
    language: String(option.value)
  });
  return /*#__PURE__*/_jsxs(Flex, {
    children: [/*#__PURE__*/_jsx(Button, {
      disabled: option.disabled,
      onClick: onSelect,
      paddingBlock: "300",
      paddingInline: "500",
      use: "unstyled",
      children: /*#__PURE__*/_jsx(View, {
        color: option.disabled ? 'text-core-disabled' : undefined,
        inlineSize: "100%",
        textAlign: "start",
        children: option.text
      })
    }), option.disabled && (trackId || lessonId) && /*#__PURE__*/_jsx(Button, {
      "data-no-full-width": "true",
      disabled: languageRequested,
      onClick: setLanguageRequested,
      paddingBlock: "300",
      paddingInline: "500",
      responsive: false,
      use: "transparent",
      children: languageRequested ? /*#__PURE__*/_jsx(FormattedMessage, {
        message: "learningCenter.requestLanguage.requested.button"
      }) : /*#__PURE__*/_jsx(FormattedMessage, {
        message: "learningCenter.requestLanguage.request.button"
      })
    })]
  }, option.value);
};
export const MobileContentLanguageSwitcher = ({
  defaultValue,
  onSelect,
  trackId,
  lessonId,
  languageLinkedContent
}) => {
  const {
    options,
    handleOptionSelection
  } = useLanguageSwitcher({
    defaultValue,
    languageLinkedContent
  });
  const handleSelection = useCallback(option => {
    if (option.disabled) {
      return;
    }
    onSelect === null || onSelect === void 0 || onSelect(option.value);
    handleOptionSelection(option);
  }, [onSelect, handleOptionSelection]);
  return /*#__PURE__*/_jsx(_Fragment, {
    children: options.map(option => /*#__PURE__*/_jsx(MobileLanguageOption, {
      lessonId: lessonId,
      onSelect: () => handleSelection(option),
      option: option,
      trackId: trackId
    }, option.value))
  });
};
export const ContentLanguageSwitcher = ({
  defaultValue,
  menuStyle,
  menuWidth,
  optionComponent,
  languageLinkedContent
}) => {
  const {
    options,
    selectedValue,
    handleSelectChange,
    handleSelectOpen
  } = useLanguageSwitcher({
    defaultValue,
    languageLinkedContent
  });
  return /*#__PURE__*/_jsx(Select, {
    buttonUse: "transparent",
    menuStyles: menuStyle,
    menuWidth: menuWidth,
    onChange: handleSelectChange,
    onOpenChange: ({
      currentTarget: {
        value
      }
    }) => handleSelectOpen(value),
    optionComponent: optionComponent,
    options: options,
    value: selectedValue
  });
};